import { Accordion, AccordionSection, Text } from '@soluto-private/mx-asurion-ui-react';
import { widgetParamsAtom } from '../storage';
import { useAtomValue } from 'jotai';
import SyntaxHighlighter from 'react-syntax-highlighter';
import { a11yDark } from 'react-syntax-highlighter/dist/esm/styles/hljs';
import styled from 'styled-components';

import contentfulFieldIdentifier from '../../assets/contentful_field_identifier.png';
import contentfulFieldChatFeature from '../../assets/contentful_field_chat-feature.png';
import contentfulFieldChatInitDataBase from '../../assets/contentful_field_chat-init-data_base.png';
import contentfulFieldChatInitDataOverrides from '../../assets/contentful_field_chat-init-data_overrides.png';

const ChatInitDataSnippet = () => {
  const widgetParams = useAtomValue(widgetParamsAtom);

  const configJson = {
    data: {
      id: widgetParams?.id ?? '{{ widget-id }}',
      variant: widgetParams?.variant ?? '{{ variant }}',
      ...(widgetParams?.variantOptionOverrides?.[widgetParams?.variant]
        ? {
            variantOptionOverrides: {
              [widgetParams?.variant]: {
                ...widgetParams?.variantOptionOverrides?.[widgetParams?.variant],
              },
            },
          }
        : {}),
    },
  };
  const configString = JSON.stringify(configJson, null, 2);

  return (
    <SyntaxHighlighter language="json" style={a11yDark} wrapLines wrapLongLines>
      {configString}
    </SyntaxHighlighter>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const SectionContainer = styled.section`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

export const ContentfulIntegration = () => {
  return (
    <Container>
      <SectionContainer>
        <Text size={3} as="h3">
          Identifier
        </Text>
        <Text size={2} as="p">
          This is the unique identifier in Contentful, make it descriptive of what variant or host page it is relevant
          to
        </Text>
        <Accordion>
          <AccordionSection subheader="Screenshot of example entry in Contentful" tabIndex={-1}>
            <img loading="lazy" src={contentfulFieldIdentifier} alt="Contentful identifier field" />
          </AccordionSection>
        </Accordion>
      </SectionContainer>
      <SectionContainer>
        <Text size={3} as="h3">
          Chat Feature
        </Text>
        <Text size={2} as="p">
          There are multiple chat "features" available on asurion.com, the SI Widget chat feature is "SI-widget"
        </Text>
        <Accordion>
          <AccordionSection subheader="Screenshot of example entry in Contentful" tabIndex={-1}>
            <img loading="lazy" src={contentfulFieldChatFeature} alt="Contentful chat feature field" />
          </AccordionSection>
        </Accordion>
      </SectionContainer>
      <SectionContainer>
        <Text size={3} as="h3">
          Chat Init Data
        </Text>
        <Text size={2} as="p">
          This is the JSON object that contains the widget configuration. This object is passed to the widget to
          configure which variant, which Simplr flow ID to use, and any overrides for the variant.
        </Text>
        <ChatInitDataSnippet />
        <Accordion>
          <AccordionSection
            subheader="Screenshot of example entry in Contentful for a base configuration"
            tabIndex={-1}
          >
            <img loading="lazy" src={contentfulFieldChatInitDataBase} alt="Contentful base chat init data field" />
          </AccordionSection>
          <AccordionSection
            subheader="Screenshot of example entry in Contentful for a configuration with overrides"
            tabIndex={-1}
          >
            <img
              loading="lazy"
              src={contentfulFieldChatInitDataOverrides}
              alt="Contentful variant overrides chat init data field"
            />
          </AccordionSection>
        </Accordion>
      </SectionContainer>
      <SectionContainer>
        <Text size={3} as="h3">
          Chat Action Props
        </Text>
        <Text size={2} as="p">
          Contentful entries can include a <code>chatActionProps</code> JSON field to trigger a chat action when the
          widget loads. Use the <code>initiateChat</code> action to open the support window with a pre-filled message.
        </Text>
        <SyntaxHighlighter language="json" style={a11yDark} wrapLines wrapLongLines>
          {JSON.stringify(
            {
              actionName: 'initiateChat',
              actionParams: {
                message: 'Help me set up my device',
              },
            },
            null,
            2
          )}
        </SyntaxHighlighter>
      </SectionContainer>
    </Container>
  );
};
